<template>
    <v-container class="mt-10" fluid>
        <!-- Page Header -->
        <div class="d-flex align-center justify-space-between mb-4">
            <h1 class="text-h4 font-weight-bold">Recruitment</h1>
            <v-btn color="primary" @click="newRequestDialog = true">
                <v-icon left>mdi-plus</v-icon>
                New Hire Request
            </v-btn>
        </div>

        <!-- Navigation Cards -->
        <v-row class="mb-6">
            <v-col v-for="nav in navItems" :key="nav.key" cols="6" sm="3">
                <v-card
                    :color="viewMode === nav.key ? 'primary' : ''"
                    :dark="viewMode === nav.key"
                    class="text-center pa-3 cursor-pointer"
                    :class="viewMode === nav.key ? 'elevation-4' : 'elevation-1'"
                    @click="viewMode = nav.key"
                    outlined
                    hover
                    style="cursor: pointer; transition: all 0.2s;"
                >
                    <v-icon :color="viewMode === nav.key ? 'white' : ''" class="mb-1">{{ nav.icon }}</v-icon>
                    <div class="text-caption font-weight-bold">{{ nav.label }}</div>
                    <div class="text-caption" :class="viewMode === nav.key ? 'white--text text--lighten-3' : 'grey--text'">
                        {{ nav.description }}
                    </div>
                </v-card>
            </v-col>
        </v-row>

        <!-- ==================== ALL REQUESTS TAB ==================== -->
        <div v-if="viewMode === 'requests'">
            <!-- Back to list when viewing detail -->
            <div v-if="selectedRequest">
                <v-btn text class="mb-4" @click="selectedRequest = null">
                    <v-icon left>mdi-arrow-left</v-icon> Back
                </v-btn>
                <!-- Job Request Detail View -->
                <JobRequestDetail
                    :request="selectedRequest"
                    :users="users ? users() : []"
                    :statuses="jobStatuses"
                    @update="handleUpdateRequest"
                    @delete="handleDeleteRequest"
                    @refresh="refreshSelectedRequest"
                />
            </div>
            <!-- Job Requests List -->
            <div v-else>
                <div class="d-flex flex-wrap align-center mb-4" style="gap: 12px;">
                    <v-text-field
                        v-model="search"
                        placeholder="Search by title or manager..."
                        prepend-inner-icon="mdi-magnify"
                        outlined
                        dense
                        hide-details
                        clearable
                        class="flex-grow-1"
                        style="max-width: 500px;"
                    />
                    <v-select
                        v-model="statusFilter"
                        :items="statusFilterOptions"
                        label="All Statuses"
                        outlined
                        dense
                        hide-details
                        style="max-width: 200px;"
                    />
                    <v-select
                        v-model="departmentFilter"
                        :items="departmentFilterOptions"
                        label="All Departments"
                        outlined
                        dense
                        hide-details
                        style="max-width: 200px;"
                    />
                </div>

                <!-- Loading -->
                <div v-if="loadingRequests" class="text-center py-12">
                    <v-progress-circular indeterminate color="primary" />
                    <p class="mt-4 grey--text">Loading job requests...</p>
                </div>

                <!-- Empty State -->
                <div v-else-if="filteredRequests.length === 0 && jobRequests.length === 0" class="text-center py-12">
                    <v-icon size="48" color="grey lighten-1">mdi-clipboard-text-outline</v-icon>
                    <p class="mt-4 grey--text">No job requests yet. Click <strong>New Hire Request</strong> to get started.</p>
                </div>

                <!-- No results after filter -->
                <div v-else-if="filteredRequests.length === 0" class="text-center py-12">
                    <v-icon size="48" color="grey lighten-1">mdi-magnify-close</v-icon>
                    <p class="mt-4 grey--text">No matching job requests found.</p>
                </div>

                <!-- Request Cards Grid -->
                <v-row v-else>
                    <v-col v-for="request in filteredRequests" :key="request.id" cols="12" md="6" lg="4">
                        <JobRequestCard
                            :request="request"
                            @click="selectJobRequest(request)"
                            @delete="promptDeleteRequest(request, $event)"
                        />
                    </v-col>
                </v-row>
            </div>
        </div>

        <!-- ==================== DEAN'S APPROVALS TAB ==================== -->
        <div v-if="viewMode === 'approvals'">
            <div class="d-flex align-center justify-space-between mb-3">
                <div>
                    <h2 class="text-h6 font-weight-bold">Dean's Recruitment Approvals</h2>
                    <p class="text-body-2 grey--text">
                        Review candidates submitted for approval.
                        <v-chip v-if="pendingApprovalCount > 0" x-small color="error" dark class="ml-1">{{ pendingApprovalCount }} pending</v-chip>
                    </p>
                </div>
                <v-btn outlined small @click="fetchDeanCandidates">
                    <v-icon left small>mdi-refresh</v-icon> Refresh
                </v-btn>
            </div>

            <!-- Filter buttons -->
            <div class="d-flex flex-wrap mb-4" style="gap: 8px;">
                <v-btn v-for="f in deanFilterOptions" :key="f.value"
                    small
                    :color="deanFilter === f.value ? 'primary' : ''"
                    :outlined="deanFilter !== f.value"
                    @click="deanFilter = f.value"
                >
                    {{ f.label }}
                </v-btn>
            </div>

            <div v-if="loadingDeanCandidates" class="text-center py-12">
                <v-progress-circular indeterminate color="primary" />
            </div>
            <div v-else-if="filteredDeanCandidates.length === 0" class="text-center py-12">
                <v-icon size="48" color="grey lighten-1">mdi-clipboard-check-outline</v-icon>
                <p class="mt-4 grey--text">No {{ deanFilter !== 'all' ? deanFilter : '' }} candidates for approval.</p>
            </div>
            <v-row v-else>
                <v-col v-for="(candidate, index) in filteredDeanCandidates" :key="candidate.id" cols="12" sm="6" lg="4" xl="3">
                    <v-card outlined hover @click="selectedDeanCandidate = candidate; deanReviewMode = 'view'; deanDetailDialog = true" style="cursor: pointer;">
                        <div
                            :style="{
                                height: '3px',
                                background: candidate.dean_approval_status === 'pending' ? '#FFC107' :
                                    candidate.dean_approval_status === 'approved' ? '#4CAF50' :
                                    candidate.dean_approval_status === 'rejected' ? '#F44336' :
                                    candidate.dean_approval_status === 'modify_offer' ? '#FF9800' : '#E0E0E0'
                            }"
                        />
                        <v-card-text class="pb-2">
                            <div class="d-flex align-center justify-space-between mb-1">
                                <div class="d-flex align-center" style="min-width: 0;">
                                    <span class="text-caption grey--text mr-1">#{{ index + 1 }}</span>
                                    <v-icon small class="mr-1">mdi-account</v-icon>
                                    <span class="font-weight-bold text-truncate">{{ candidate.name }}</span>
                                </div>
                                <div class="d-flex align-center" style="gap: 6px;">
                                    <v-chip x-small
                                        :color="getDeanStatusColor(candidate.dean_approval_status)"
                                        :dark="candidate.dean_approval_status !== 'modify_offer'"
                                        class="text-capitalize"
                                    >
                                        {{ candidate.dean_approval_status === 'modify_offer' ? 'Modify Offer' : candidate.dean_approval_status }}
                                    </v-chip>
                                    <v-btn icon x-small color="error" @click.stop="promptDeleteCandidate(candidate, $event)" title="Delete candidate">
                                        <v-icon x-small>mdi-delete-outline</v-icon>
                                    </v-btn>
                                </div>
                            </div>
                            <div class="text-caption grey--text mb-2">{{ candidate.email }}</div>
                            <v-row dense class="text-caption">
                                <v-col cols="4">
                                    <span class="grey--text d-block" style="font-size: 9px;">ROLE</span>
                                    <span class="font-weight-medium text-truncate d-block">{{ candidate.job_request?.job_title || '—' }}</span>
                                </v-col>
                                <v-col cols="4">
                                    <span class="grey--text d-block" style="font-size: 9px;">MANAGER</span>
                                    <span class="font-weight-medium text-truncate d-block">{{ candidate.job_request?.hiring_manager_name || '—' }}</span>
                                </v-col>
                                <v-col cols="4">
                                    <span class="grey--text d-block" style="font-size: 9px;">SALARY</span>
                                    <span class="font-weight-medium text-truncate d-block">{{ candidate.salary_expectation || '—' }}</span>
                                </v-col>
                            </v-row>
                            <div class="d-flex align-center justify-space-between mt-2">
                                <div>
                                    <v-icon v-for="r in 5" :key="r" x-small :color="r <= (candidate.rating || 0) ? 'amber' : 'grey lighten-2'">
                                        mdi-star
                                    </v-icon>
                                </div>
                                <div class="d-flex" style="gap: 4px;">
                                    <v-icon v-if="candidate.cv_file_url" x-small color="primary" title="CV uploaded">mdi-file-eye-outline</v-icon>
                                    <v-icon v-if="candidate.job_offer_url" x-small color="teal" title="Offer uploaded">mdi-file-document-check-outline</v-icon>
                                </div>
                            </div>
                            <!-- AI Analysis Preview -->
                            <div v-if="candidate.ai_cv_summary" class="mt-2 pa-2 rounded" style="background: #F5F5F5; font-size: 11px;">
                                <div class="d-flex align-center mb-1">
                                    <v-icon x-small color="primary" class="mr-1">mdi-robot-outline</v-icon>
                                    <span class="grey--text text--darken-2 font-weight-medium" style="font-size: 10px;">AI ANALYSIS</span>
                                </div>
                                <span class="grey--text text--darken-1" style="display: -webkit-box; -webkit-line-clamp: 2; line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;">
                                    {{ candidate.ai_cv_summary }}
                                </span>
                                <a class="primary--text mt-1 d-block" style="font-size: 10px; cursor: pointer;" @click.stop="selectedDeanCandidate = candidate; deanReviewMode = 'view'; deanDetailDialog = true">
                                    Read full analysis →
                                </a>
                            </div>
                        </v-card-text>
                        <v-card-actions class="pt-0 d-flex align-center justify-space-between">
                            <div class="d-flex" style="gap: 4px;">
                            <template v-if="(isCeo || get_user.id === 42 || get_user.id === 177) && candidate.dean_approval_status === 'pending'">
                            <v-btn x-small color="success" @click.stop="openDeanReview(candidate, 'approving')"><!-- dean approve -->
                                <v-icon left x-small>mdi-check-circle</v-icon> Approve
                            </v-btn>
                            <v-btn x-small outlined @click.stop="modifyOfferCandidate = candidate; modifyOfferNotes = ''; modifyOfferDialog = true">
                                <v-icon left x-small>mdi-pencil</v-icon> Modify Offer
                            </v-btn>
                            <v-btn x-small color="error" @click.stop="openDeanReview(candidate, 'rejecting')">
                                <v-icon left x-small>mdi-close-circle</v-icon> Reject
                            </v-btn>
                            </template>
                            </div>
                        </v-card-actions>
                    </v-card>
                </v-col>
            </v-row>
        </div>

        <!-- ==================== PIPELINE TAB ==================== -->
        <div v-if="viewMode === 'pipeline'">
            <h2 class="text-h6 font-weight-bold mb-4">Candidate Pipeline</h2>
            <div v-if="loadingRequests" class="text-center py-12">
                <v-progress-circular indeterminate color="primary" />
            </div>
            <div v-else>
                <div class="pipeline-container" style="overflow-x: auto;">
                    <div class="d-flex" style="gap: 12px; min-width: fit-content;">
                        <div v-for="stage in pipelineStages" :key="stage.value"
                            style="min-width: 250px; max-width: 300px; flex: 1;"
                        >
                            <v-card outlined class="mb-4">
                                <v-card-title class="py-2 px-3" :style="{ borderBottom: '3px solid ' + stage.color }">
                                    <span class="text-subtitle-2 font-weight-bold">{{ stage.label }}</span>
                                    <v-spacer />
                                    <v-chip x-small>{{ (pipelineGroupsData[stage.value] || []).length }}</v-chip>
                                </v-card-title>
                                <v-card-text class="pa-2 pipeline-drop-zone" style="min-height: 200px; max-height: 500px; overflow-y: auto;">
                                    <draggable
                                        :list="pipelineGroupsData[stage.value]"
                                        group="pipeline"
                                        ghost-class="pipeline-ghost"
                                        drag-class="pipeline-drag"
                                        :animation="200"
                                        style="min-height: 150px; display: block;"
                                        @change="onPipelineDrop($event, stage.value)"
                                    >
                                        <v-card v-for="c in pipelineGroupsData[stage.value]" :key="c.id"
                                            outlined class="mb-2 pa-2 pipeline-card"
                                            @click.stop="selectedDeanCandidate = c; deanDetailDialog = true"
                                        >
                                            <div class="d-flex align-start" style="gap:6px;">
                                                <div class="flex-grow-1" style="min-width:0;">
                                                    <div class="font-weight-medium text-body-2 text-truncate">{{ c.name }}</div>
                                                    <div class="text-caption grey--text text-truncate">{{ c.job_request?.job_title || 'No role' }}</div>
                                                </div>
                                                <v-btn icon x-small color="error" title="Delete candidate"
                                                    class="pipeline-card-delete flex-shrink-0"
                                                    @click.stop="promptDeleteCandidate(c, $event)">
                                                    <v-icon x-small>mdi-delete-outline</v-icon>
                                                </v-btn>
                                            </div>
                                            <div class="d-flex align-center mt-1">
                                                <v-icon v-for="r in 5" :key="r" x-small :color="r <= (c.rating || 0) ? 'amber' : 'grey lighten-2'">mdi-star</v-icon>
                                                <v-spacer />
                                                <span class="text-caption grey--text">{{ c.source || '' }}</span>
                                            </div>
                                        </v-card>
                                        <!-- Empty state inside draggable so it stays a valid drop target -->
                                        <div v-if="!pipelineGroupsData[stage.value] || pipelineGroupsData[stage.value].length === 0"
                                            class="text-center py-6"
                                            style="pointer-events: none;"
                                        >
                                            <v-icon color="grey lighten-2">mdi-tray</v-icon>
                                            <div class="text-caption grey--text mt-1">Drop here</div>
                                        </div>
                                    </draggable>
                                </v-card-text>
                            </v-card>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- ==================== KPIs TAB ==================== -->
        <div v-if="viewMode === 'kpis'">
            <h2 class="text-h6 font-weight-bold mb-4">Recruitment KPIs</h2>
            <v-row>
                <v-col cols="12" sm="6" md="3">
                    <v-card outlined class="text-center pa-4">
                        <v-icon size="36" color="primary" class="mb-2">mdi-clipboard-list</v-icon>
                        <div class="text-h4 font-weight-bold">{{ jobRequests.length }}</div>
                        <div class="text-caption grey--text">Total Requests</div>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                    <v-card outlined class="text-center pa-4">
                        <v-icon size="36" color="success" class="mb-2">mdi-briefcase-check</v-icon>
                        <div class="text-h4 font-weight-bold">{{ jobRequests.filter(r => r.status === 'Closed').length }}</div>
                        <div class="text-caption grey--text">Positions Filled</div>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                    <v-card outlined class="text-center pa-4">
                        <v-icon size="36" color="amber" class="mb-2">mdi-account-group</v-icon>
                        <div class="text-h4 font-weight-bold">{{ allCandidates.length }}</div>
                        <div class="text-caption grey--text">Total Candidates</div>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                    <v-card outlined class="text-center pa-4">
                        <v-icon size="36" color="info" class="mb-2">mdi-calendar-clock</v-icon>
                        <div class="text-h4 font-weight-bold">{{ avgDaysToHire }}</div>
                        <div class="text-caption grey--text">Avg. Days to Hire</div>
                    </v-card>
                </v-col>
            </v-row>
            <!-- Status breakdown -->
            <v-card outlined class="mt-4">
                <v-card-title class="text-subtitle-1 font-weight-bold">Requests by Status</v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col v-for="status in jobStatuses" :key="status" cols="6" sm="4" md="3">
                            <div class="d-flex align-center mb-1">
                                <v-chip x-small :color="getRequestStatusCount(status) > 0 ? getStatusColor(status) : 'grey lighten-1'" :dark="getRequestStatusCount(status) > 0" :outlined="getRequestStatusCount(status) === 0" class="mr-2">{{ getRequestStatusCount(status) }}</v-chip>
                                <span class="text-caption" :class="getRequestStatusCount(status) === 0 ? 'grey--text text--lighten-1' : ''">{{ status }}</span>
                            </div>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
            <!-- Candidates by source -->
            <v-card outlined class="mt-4">
                <v-card-title class="text-subtitle-1 font-weight-bold">Candidates by Source</v-card-title>
                <v-card-text>
                    <v-row>
                        <v-col v-for="(count, source) in candidatesBySource" :key="source" cols="6" sm="4" md="3">
                            <div class="d-flex align-center mb-1">
                                <v-chip x-small color="primary" dark class="mr-2">{{ count }}</v-chip>
                                <span class="text-caption">{{ source || 'Unknown' }}</span>
                            </div>
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>
        </div>

        <!-- ==================== NEW HIRE REQUEST DIALOG ==================== -->
        <v-dialog v-model="newRequestDialog" max-width="700" scrollable>
            <v-card>
                <v-card-title class="primary white--text">
                    <v-icon left color="white">mdi-plus-circle</v-icon>
                    New Hire Request
                    <v-spacer />
                    <v-btn icon dark @click="newRequestDialog = false"><v-icon>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text class="pa-4">
                    <!-- Step Tabs -->
                    <div class="d-flex justify-center mb-4" style="gap: 8px;">
                        <v-chip v-for="(step, idx) in formSteps" :key="idx"
                            :color="newRequestStep === idx ? 'primary' : 'default'"
                            :dark="newRequestStep === idx"
                            :outlined="newRequestStep !== idx"
                            small
                            @click="newRequestStep = idx"
                        >{{ step }}</v-chip>
                    </div>

                    <!-- Step 0: Basic Info -->
                    <div v-show="newRequestStep === 0">
                        <v-text-field v-model="newRequest.job_title" label="Job Title *" placeholder="e.g. Senior Developer" outlined dense />
                        <v-row>
                            <v-col cols="6"><v-text-field v-model="newRequest.department" label="Department" placeholder="e.g. Engineering" outlined dense /></v-col>
                            <v-col cols="6"><v-text-field v-model="newRequest.portfolio" label="Company / Portfolio" placeholder="e.g. PT, STAGIT" outlined dense /></v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="6">
                                <v-select v-model="newRequest.priority" :items="['Low', 'Normal', 'High', 'Urgent']" label="Priority" outlined dense />
                            </v-col>
                            <v-col cols="6">
                                <v-select v-model="newRequest.employment_type" :items="employmentTypes" label="Employment Type" outlined dense />
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="6"><v-text-field v-model="newRequest.hiring_manager_name" label="Hiring Manager Name" outlined dense /></v-col>
                            <v-col cols="6"><v-text-field v-model="newRequest.requester_email" label="Requester Email" outlined dense /></v-col>
                        </v-row>
                        <v-textarea v-model="newRequest.reason_for_hire" label="Reason for Hire" placeholder="Primary reason for this hire" outlined dense rows="3" />
                    </div>

                    <!-- Step 1: Job Details -->
                    <div v-show="newRequestStep === 1">
                        <v-textarea v-model="newRequest.job_description" label="Job Description" placeholder="Core responsibilities, expectations..." outlined dense rows="6" />
                        <v-text-field v-model="newRequest.experience" label="Experience Level" placeholder="e.g. 3-5 years, Senior, Entry-Level" outlined dense />
                        <div class="text-subtitle-2 mb-1">Salary Range</div>
                        <v-row>
                            <v-col cols="4">
                                <v-select v-model="newRequest.salary_currency" :items="['$ USD', '€ EUR', '£ GBP', 'AED', 'PHP']" outlined dense />
                            </v-col>
                            <v-col cols="8">
                                <v-text-field v-model="newRequest.salary_range" placeholder="e.g. 80,000 - 100,000" outlined dense />
                            </v-col>
                        </v-row>
                        <v-textarea v-model="newRequest.success_criteria" label="Success in First 90 Days" placeholder="What does success look like?" outlined dense rows="3" />
                    </div>

                    <!-- Step 2: Requirements -->
                    <div v-show="newRequestStep === 2">
                        <v-textarea v-model="newRequest.mandatory_skills" label="Mandatory Skills (Must-haves)" placeholder="Required skills and technologies" outlined dense rows="4" />
                        <v-text-field v-model="newRequest.other_mandatory_skills" label="Other Mandatory Skills" outlined dense />
                        <v-textarea v-model="newRequest.preferred_qualifications" label="Preferred Qualifications (Nice-to-haves)" outlined dense rows="4" />
                        <v-text-field v-model="newRequest.nationality_preference" label="Preferred Nationality" outlined dense />

                        <v-card outlined class="pa-4 mt-2">
                            <div class="text-subtitle-2 font-weight-bold mb-3">Candidate Attribute Priorities (1-5)</div>
                            <div v-for="attr in attributeSliders" :key="attr.key" class="d-flex align-center mb-1" style="gap: 12px;">
                                <span class="text-caption" style="min-width: 130px;">{{ attr.label }}</span>
                                <v-slider v-model="newRequest[attr.key]" :min="1" :max="5" :step="1" hide-details dense thumb-label style="flex: 1;" />
                                <span class="text-body-2 font-weight-medium" style="min-width: 20px;">{{ newRequest[attr.key] }}</span>
                            </div>
                        </v-card>
                    </div>

                    <!-- Step 3: Interview Setup -->
                    <div v-show="newRequestStep === 3">
                        <v-textarea v-model="newRequest.interview_panel" label="Interview Panel" placeholder="Team members involved in interviews" outlined dense rows="4" />
                        <v-text-field v-model="newRequest.contact_person" label="Primary Contact for Recruitment" outlined dense />
                        <v-textarea v-model="newRequest.interview_notes" label="Other Information" placeholder="Anything else to help find the right candidate" outlined dense rows="4" />
                        <v-card outlined class="pa-3 mt-1">
                            <div class="d-flex align-center justify-space-between">
                                <span class="text-body-2">Requires Dean Approval</span>
                                <v-switch v-model="newRequest.requires_dean_approval" hide-details dense class="mt-0 pt-0" />
                            </div>
                        </v-card>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn v-if="newRequestStep > 0" text @click="newRequestStep--">
                        <v-icon left>mdi-chevron-left</v-icon> Back
                    </v-btn>
                    <v-btn v-if="newRequestStep < formSteps.length - 1" color="primary" @click="newRequestStep++">
                        Next <v-icon right>mdi-chevron-right</v-icon>
                    </v-btn>
                    <v-btn v-else color="primary" :loading="savingRequest" :disabled="!newRequest.job_title" @click="createJobRequest">
                        <v-icon left>mdi-check</v-icon> Submit Request
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- ==================== DEAN DETAIL / REVIEW DIALOG ==================== -->
        <v-dialog v-model="deanDetailDialog" max-width="680">
            <v-card v-if="selectedDeanCandidate">
                <!-- Header with status badge -->
                <v-card-text class="pt-4 pb-0 px-5">
                    <div class="d-flex align-start justify-space-between">
                        <div>
                            <div class="d-flex align-center" style="gap: 10px;">
                                <h2 class="text-h6 font-weight-bold mb-0">{{ selectedDeanCandidate.name }}</h2>
                                <v-chip v-if="deanReviewMode === 'approving'" small color="success" dark class="font-weight-bold">Approving</v-chip>
                                <v-chip v-else-if="deanReviewMode === 'rejecting'" small color="error" dark class="font-weight-bold">Rejecting</v-chip>
                                <v-chip v-else-if="selectedDeanCandidate.dean_approval_status && selectedDeanCandidate.dean_approval_status !== 'pending'" small
                                    :color="getDeanStatusColor(selectedDeanCandidate.dean_approval_status)"
                                    :dark="selectedDeanCandidate.dean_approval_status !== 'modify_offer'"
                                    class="text-capitalize font-weight-bold"
                                >
                                    {{ selectedDeanCandidate.dean_approval_status === 'modify_offer' ? 'Modify Offer' : selectedDeanCandidate.dean_approval_status }}
                                </v-chip>
                            </div>
                            <p class="text-caption grey--text text--darken-1 mb-0">
                                {{ selectedDeanCandidate.job_request?.job_title || '' }}
                                <span v-if="selectedDeanCandidate.job_request?.department"> &bull; {{ selectedDeanCandidate.job_request.department }}</span>
                            </p>
                        </div>
                        <v-btn icon small @click="deanDetailDialog = false"><v-icon small>mdi-close</v-icon></v-btn>
                    </div>
                </v-card-text>

                <!-- Info Grid -->
                <v-card-text class="px-5 pt-3 pb-2">
                    <v-row dense>
                        <v-col v-if="selectedDeanCandidate.email" cols="6">
                            <span class="d-block grey--text font-weight-bold" style="font-size: 11px;">Email</span>
                            <span class="text-body-2">{{ selectedDeanCandidate.email }}</span>
                        </v-col>
                        <v-col v-if="selectedDeanCandidate.phone" cols="6">
                            <span class="d-block grey--text font-weight-bold" style="font-size: 11px;">Phone</span>
                            <span class="text-body-2">{{ selectedDeanCandidate.phone }}</span>
                        </v-col>
                        <v-col v-if="selectedDeanCandidate.source" cols="6" class="mt-2">
                            <span class="d-block grey--text font-weight-bold" style="font-size: 11px;">Source</span>
                            <span class="text-body-2">{{ selectedDeanCandidate.source }}</span>
                        </v-col>
                        <v-col v-if="selectedDeanCandidate.job_request?.hiring_manager_name" cols="6" class="mt-2">
                            <span class="d-block grey--text font-weight-bold" style="font-size: 11px;">Hiring Manager</span>
                            <span class="text-body-2">{{ selectedDeanCandidate.job_request.hiring_manager_name }}</span>
                        </v-col>
                        <v-col v-if="selectedDeanCandidate.salary_expectation" cols="6" class="mt-2">
                            <span class="d-block grey--text font-weight-bold" style="font-size: 11px;">Expected Salary</span>
                            <span class="text-body-2">{{ selectedDeanCandidate.salary_expectation }}</span>
                        </v-col>
                        <v-col cols="6" class="mt-2">
                            <span class="d-block grey--text font-weight-bold" style="font-size: 11px;">Recruiter Rating</span>
                            <div>
                                <v-icon v-for="r in 5" :key="r" small :color="r <= (selectedDeanCandidate.rating || 0) ? 'amber darken-1' : 'grey lighten-2'">mdi-star</v-icon>
                            </div>
                        </v-col>
                    </v-row>

                    <!-- CV / Offer Buttons -->
                    <div class="d-flex mt-3" style="gap: 10px;">
                        <v-btn v-if="selectedDeanCandidate.cv_file_url" outlined small @click="openFileViewer(selectedDeanCandidate.cv_file_url, 'CV — ' + selectedDeanCandidate.name)">
                            <v-icon left small>mdi-eye-outline</v-icon> View CV
                        </v-btn>
                        <v-btn v-else outlined small disabled>
                            <v-icon left small>mdi-eye-off-outline</v-icon> No CV
                        </v-btn>
                        <v-btn v-if="selectedDeanCandidate.job_offer_url" outlined small @click="openFileViewer(selectedDeanCandidate.job_offer_url, 'Offer — ' + selectedDeanCandidate.name)">
                            <v-icon left small>mdi-eye-outline</v-icon> View Offer
                        </v-btn>
                        <v-btn v-else outlined small disabled>
                            <v-icon left small>mdi-eye-off-outline</v-icon> No Offer
                        </v-btn>
                        <v-btn v-if="selectedDeanCandidate.recording_link" outlined small color="primary" :href="selectedDeanCandidate.recording_link" target="_blank">
                            <v-icon left small>mdi-video-outline</v-icon> View Recording
                        </v-btn>
                    </div>
                </v-card-text>

                <v-divider class="mx-5" />

                <!-- AI CV Analysis Card -->
                <v-card-text class="px-5 pt-3 pb-2">
                    <v-card outlined class="pa-3" style="background: #FAFAFA; border-radius: 8px;">
                        <div class="d-flex align-center mb-2">
                            <v-icon color="primary" small class="mr-1">mdi-robot-outline</v-icon>
                            <span class="font-weight-bold text-body-2">AI CV Analysis</span>
                        </div>

                        <div v-if="analyzingDeanCv" class="d-flex align-center py-2" style="gap: 8px;">
                            <v-progress-circular indeterminate color="primary" size="18" width="2" />
                            <span class="text-caption grey--text">Analyzing CV with AI...</span>
                        </div>

                        <div v-else-if="selectedDeanCandidate.ai_cv_summary"
                            class="text-body-2 pr-1"
                            style="white-space: pre-line; line-height: 1.55; max-height: 220px; overflow-y: auto;"
                        >{{ selectedDeanCandidate.ai_cv_summary }}</div>

                        <div v-else class="text-caption grey--text py-2">
                            {{ selectedDeanCandidate.cv_file_url ? 'No AI analysis yet.' : 'No CV uploaded.' }}
                        </div>
                    </v-card>
                </v-card-text>

                <!-- Notes (only if present) -->
                <v-card-text v-if="selectedDeanCandidate.notes || selectedDeanCandidate.dean_approval_notes" class="px-5 pt-0 pb-2">
                    <div v-if="selectedDeanCandidate.notes" class="d-flex" style="gap: 4px;">
                        <span class="text-caption font-weight-bold grey--text text--darken-2 text-no-wrap">Recruiter:</span>
                        <span class="text-caption grey--text text--darken-1">{{ selectedDeanCandidate.notes }}</span>
                    </div>
                    <div v-if="selectedDeanCandidate.dean_approval_notes" class="d-flex mt-1" style="gap: 4px;">
                        <span class="text-caption font-weight-bold grey--text text--darken-2 text-no-wrap">Dean:</span>
                        <span class="text-caption grey--text text--darken-1">{{ selectedDeanCandidate.dean_approval_notes }}</span>
                    </div>
                </v-card-text>

                <!-- Action Buttons -->
                <v-divider class="mx-5" />
                <v-card-actions class="px-5 py-4 d-flex align-center" style="gap: 12px;">
                    <v-btn outlined small @click="openEditCandidate(selectedDeanCandidate)">
                        <v-icon left small>mdi-pencil</v-icon> Edit Info
                    </v-btn>
                    <v-spacer />
                    <template v-if="isCeo">
                        <!-- Approving mode -->
                        <template v-if="deanReviewMode === 'approving'">
                            <v-btn text @click="deanDetailDialog = false">Cancel</v-btn>
                            <v-btn color="success" class="px-6 font-weight-bold" :loading="processingDecision" @click="handleDeanDecision(selectedDeanCandidate, 'approved'); deanDetailDialog = false">
                                <v-icon left>mdi-check-circle-outline</v-icon> Confirm Approval
                            </v-btn>
                        </template>
                        <!-- Rejecting mode -->
                        <template v-else-if="deanReviewMode === 'rejecting'">
                            <v-btn text @click="deanDetailDialog = false">Cancel</v-btn>
                            <v-btn color="error" class="px-6 font-weight-bold" :loading="processingDecision" @click="handleDeanDecision(selectedDeanCandidate, 'rejected'); deanDetailDialog = false">
                                <v-icon left>mdi-close-circle-outline</v-icon> Confirm Rejection
                            </v-btn>
                        </template>
                        <!-- View mode (pending) -->
                        <template v-else-if="selectedDeanCandidate.dean_approval_status === 'pending'">
                            <v-btn color="success" class="px-5" @click="deanReviewMode = 'approving'">
                                <v-icon left>mdi-check-circle-outline</v-icon> Approve
                            </v-btn>
                            <v-btn outlined class="px-5" @click="modifyOfferCandidate = selectedDeanCandidate; modifyOfferNotes = ''; deanDetailDialog = false; modifyOfferDialog = true">
                                <v-icon left>mdi-pencil</v-icon> Modify Offer
                            </v-btn>
                            <v-btn color="error" class="px-5" @click="deanReviewMode = 'rejecting'">
                                <v-icon left>mdi-close-circle-outline</v-icon> Reject
                            </v-btn>
                        </template>
                        <!-- Already decided -->
                        <template v-else>
                            <v-chip
                                :color="getDeanStatusColor(selectedDeanCandidate.dean_approval_status)"
                                :dark="selectedDeanCandidate.dean_approval_status !== 'modify_offer'"
                                class="text-capitalize"
                            >
                                {{ selectedDeanCandidate.dean_approval_status === 'modify_offer' ? 'Modification Requested' : selectedDeanCandidate.dean_approval_status }}
                            </v-chip>
                        </template>
                    </template>
                    <!-- Non-CEO: just show current status -->
                    <template v-else>
                        <v-chip
                            :color="getDeanStatusColor(selectedDeanCandidate.dean_approval_status)"
                            :dark="selectedDeanCandidate.dean_approval_status !== 'modify_offer' && selectedDeanCandidate.dean_approval_status !== 'pending'"
                            :outlined="selectedDeanCandidate.dean_approval_status === 'pending'"
                            class="text-capitalize"
                        >
                            {{ selectedDeanCandidate.dean_approval_status === 'modify_offer' ? 'Modification Requested' : selectedDeanCandidate.dean_approval_status }}
                        </v-chip>
                    </template>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- ==================== EDIT CANDIDATE DIALOG ==================== -->
        <v-dialog v-model="editCandidateDialog" max-width="560" persistent>
            <v-card>
                <v-card-title class="d-flex align-center">
                    <v-icon color="primary" class="mr-2">mdi-account-edit</v-icon>
                    <span>Edit Candidate</span>
                    <v-spacer />
                    <v-btn icon small @click="editCandidateDialog = false"><v-icon small>mdi-close</v-icon></v-btn>
                </v-card-title>
                <v-card-text class="pt-2">
                    <v-row dense>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="editCandidateForm.name" label="Full Name" outlined dense hide-details class="mb-3" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="editCandidateForm.email" label="Email" outlined dense hide-details class="mb-3" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="editCandidateForm.phone" label="Phone" outlined dense hide-details class="mb-3" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field v-model="editCandidateForm.salary_expectation" label="Salary Expectation" outlined dense hide-details class="mb-3" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-select v-model="editCandidateForm.source" label="Source" :items="['Job Board', 'Onlinejobs.ph', 'LinkedIn', 'Referral', 'Website', 'Other']" outlined dense hide-details class="mb-3" />
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-select v-model="editCandidateForm.status" label="Pipeline Status" :items="pipelineStages.map(s => s.value)" outlined dense hide-details class="mb-3" />
                        </v-col>
                        <v-col cols="12">
                            <div class="text-caption grey--text font-weight-bold mb-1">Rating</div>
                            <div class="d-flex align-center" style="gap: 2px;">
                                <v-btn v-for="r in 5" :key="r" icon small @click="editCandidateForm.rating = r">
                                    <v-icon :color="r <= editCandidateForm.rating ? 'amber darken-1' : 'grey lighten-2'">mdi-star</v-icon>
                                </v-btn>
                                <v-btn v-if="editCandidateForm.rating" icon x-small @click="editCandidateForm.rating = 0" class="ml-1" title="Clear rating">
                                    <v-icon x-small>mdi-close</v-icon>
                                </v-btn>
                            </div>
                        </v-col>
                        <v-col cols="12" class="mt-2">
                            <v-textarea v-model="editCandidateForm.notes" label="Notes" outlined dense rows="3" auto-grow hide-details />
                        </v-col>
                    </v-row>
                </v-card-text>
                <v-card-actions class="px-6 pb-4">
                    <v-spacer />
                    <v-btn text @click="editCandidateDialog = false">Cancel</v-btn>
                    <v-btn color="primary" :loading="savingCandidate" @click="saveEditCandidate">
                        <v-icon left small>mdi-content-save</v-icon> Save Changes
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- ==================== CONFIRM DELETE DIALOG ==================== -->
        <v-dialog v-model="confirmDeleteDialog" max-width="400" persistent>
            <v-card>
                <v-card-title class="d-flex align-center">
                    <v-icon color="error" class="mr-2">mdi-delete-alert</v-icon>
                    <span>Confirm Delete</span>
                </v-card-title>
                <v-card-text>
                    <span v-if="deleteItemType === 'request'">
                        Are you sure you want to delete the job request <strong>"{{ deleteItem && deleteItem.job_title }}"</strong>? This will also delete all associated candidates.
                    </span>
                    <span v-else-if="deleteItemType === 'candidate'">
                        Are you sure you want to delete candidate <strong>"{{ deleteItem && deleteItem.name }}"</strong>? This action cannot be undone.
                    </span>
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="confirmDeleteDialog = false; deleteItem = null">Cancel</v-btn>
                    <v-btn color="error" :loading="deletingItem" @click="executeDelete">
                        <v-icon left small>mdi-delete</v-icon> Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- ==================== FILE VIEWER DIALOG ==================== -->
        <v-dialog v-model="fileViewerDialog" max-width="900" content-class="file-viewer-dialog">
            <v-card style="height: 85vh; display: flex; flex-direction: column; overflow: hidden;">
                <!-- Loading state while fetching file -->
                <div v-if="fileViewerLoading" class="d-flex align-center justify-center" style="flex: 1;">
                    <v-progress-circular indeterminate color="primary" />
                    <span class="ml-3 text-body-2 grey--text">Loading document...</span>
                </div>

                <!-- PDF files — rendered via PdfViewer canvas -->
                <PdfViewer
                    v-else-if="fileViewerPdfData"
                    :pdf-data="fileViewerPdfData"
                    :src="fileViewerUrl"
                    style="flex: 1; min-height: 0;"
                />

                <!-- Image files — rendered via <img> -->
                <template v-else-if="fileViewerBlobUrl && fileViewerIsImage">
                    <v-card-title class="py-2 px-4 d-flex align-center" style="flex: 0;">
                        <v-icon left small>mdi-image</v-icon>
                        <span class="text-subtitle-2 text-truncate">{{ fileViewerTitle }}</span>
                        <v-spacer />
                        <v-btn icon small @click="fileViewerDialog = false">
                            <v-icon small>mdi-close</v-icon>
                        </v-btn>
                    </v-card-title>
                    <v-divider />
                    <div style="flex: 1; overflow: auto; display: flex; align-items: center; justify-content: center; background: #f5f5f5;">
                        <img :src="fileViewerBlobUrl" style="max-width: 100%; max-height: 100%; object-fit: contain;" />
                    </div>
                </template>

                <!-- Error / unsupported -->
                <div v-else-if="fileViewerError" class="d-flex flex-column align-center justify-center pa-6" style="flex: 1;">
                    <v-icon size="48" color="error">mdi-file-alert-outline</v-icon>
                    <p class="mt-3 text-body-2 grey--text text-center">{{ fileViewerError }}</p>
                    <v-btn icon small @click="fileViewerDialog = false" class="mt-2"><v-icon>mdi-close</v-icon></v-btn>
                </div>
            </v-card>
        </v-dialog>

        <!-- ==================== MODIFY OFFER DIALOG ==================== -->
        <v-dialog v-model="modifyOfferDialog" max-width="500">
            <v-card>
                <v-card-title>
                    <v-icon left>mdi-pencil</v-icon> Modify Offer Request
                </v-card-title>
                <v-card-subtitle v-if="modifyOfferCandidate">
                    Add clear modification instructions for {{ modifyOfferCandidate.name }}.
                </v-card-subtitle>
                <v-card-text>
                    <v-textarea
                        v-model="modifyOfferNotes"
                        placeholder="e.g. revise salary to AED 25,000/month and upload an updated offer letter"
                        outlined
                        dense
                        rows="4"
                        autofocus
                    />
                </v-card-text>
                <v-card-actions>
                    <v-spacer />
                    <v-btn text @click="modifyOfferDialog = false">Cancel</v-btn>
                    <v-btn outlined :disabled="!modifyOfferNotes.trim()" :loading="processingDecision"
                        @click="handleDeanDecision(modifyOfferCandidate, 'modify_offer', modifyOfferNotes); modifyOfferDialog = false">
                        <v-icon left>mdi-pencil</v-icon> Request Modification
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { mapGetters } from 'vuex';
import axios from 'axios';
import draggable from 'vuedraggable';
import JobRequestCard from './components/JobRequestCard.vue';
import JobRequestDetail from './components/JobRequestDetail.vue';
import PdfViewer from '../modules/PdfViewer.vue';
import { extractPdfContent, extractImageContent } from '@/services/pdfExtractor';

export default {
    name: 'RecruitmentComponent',
    inject: ['users'],
    components: {
        draggable,
        JobRequestCard,
        JobRequestDetail,
        PdfViewer,
    },
    data() {
        return {
            viewMode: 'requests',
            navItems: [
                { key: 'approvals', icon: 'mdi-shield-check', label: "Dean's Approvals", description: 'Review & approve candidates' },
                { key: 'requests', icon: 'mdi-clipboard-list', label: 'All Requests', description: 'Manage job requests' },
                { key: 'pipeline', icon: 'mdi-source-branch', label: 'Pipeline', description: 'Candidate pipeline view' },
                { key: 'kpis', icon: 'mdi-chart-bar', label: 'KPIs', description: 'Recruitment metrics' },
            ],
            // All Requests
            jobRequests: [],
            loadingRequests: false,
            search: '',
            statusFilter: 'all',
            departmentFilter: 'all',
            selectedRequest: null,
            newRequestDialog: false,
            savingRequest: false,
            newRequestStep: 0,
            formSteps: ['Basic Info', 'Job Details', 'Requirements', 'Interview Setup'],
            attributeSliders: [
                { key: 'attribute_technical_skills', label: 'Technical Skills' },
                { key: 'attribute_cultural_fit', label: 'Cultural Fit' },
                { key: 'attribute_problem_solving', label: 'Problem Solving' },
                { key: 'attribute_communication', label: 'Communication' },
                { key: 'attribute_industry_experience', label: 'Industry Experience' },
            ],
            newRequest: {
                job_title: '',
                department: '',
                portfolio: '',
                priority: 'Normal',
                employment_type: 'Full-Time',
                hiring_manager_name: '',
                requester_email: '',
                reason_for_hire: '',
                job_description: '',
                experience: '',
                salary_currency: '$ USD',
                salary_range: '',
                success_criteria: '',
                mandatory_skills: '',
                other_mandatory_skills: '',
                preferred_qualifications: '',
                nationality_preference: '',
                attribute_technical_skills: 3,
                attribute_cultural_fit: 3,
                attribute_problem_solving: 3,
                attribute_communication: 3,
                attribute_industry_experience: 3,
                interview_panel: '',
                contact_person: '',
                interview_notes: '',
                requires_dean_approval: false,
            },
            // Dean's Approvals
            deanCandidates: [],
            loadingDeanCandidates: false,
            deanFilter: 'pending',
            selectedDeanCandidate: null,
            deanDetailDialog: false,
            deanReviewMode: 'view',
            modifyOfferCandidate: null,
            modifyOfferDialog: false,
            modifyOfferNotes: '',
            processingDecision: false,
            analyzingDeanCv: false,
            fileViewerDialog: false,
            fileViewerUrl: '',
            fileViewerTitle: '',
            fileViewerPdfData: null,
            fileViewerBlobUrl: null,
            fileViewerLoading: false,
            fileViewerError: null,
            deanFilterOptions: [
                { label: 'Pending', value: 'pending' },
                { label: 'Approved', value: 'approved' },
                { label: 'Rejected', value: 'rejected' },
                { label: 'Modify Offer', value: 'modify_offer' },
                { label: 'All', value: 'all' },
            ],
            // All Candidates (for pipeline/KPIs)
            allCandidates: [],
            // Job Statuses
            jobStatuses: [
                'Draft', 'Submitted', 'Job Posted', 'Applicants Under Review',
                'Shortlist Created', 'Interviews In Progress', 'Final Decision Pending',
                'Approved', 'Offer Accepted', 'Onboarding In Progress', 'Closed'
            ],
            employmentTypes: ['Full-Time', 'Part-Time', 'Contract', 'Internship'],
            pipelineStages: [
                { value: 'Applied', label: 'Applied', color: '#2196F3' },
                { value: 'Shortlisted', label: 'Shortlisted', color: '#FF9800' },
                { value: 'Interview 1 Done', label: 'Interview 1', color: '#9C27B0' },
                { value: 'Interview 2 Done', label: 'Interview 2', color: '#673AB7' },
                { value: "For Dean's Approval", label: "Dean's Approval", color: '#FFC107' },
                { value: 'Offer Modification Requested', label: 'Modify Offer', color: '#FF5722' },
                { value: 'Approved', label: 'Approved', color: '#8BC34A' },
                { value: 'Offer Extended', label: 'Offer Extended', color: '#4CAF50' },
                { value: 'Hired', label: 'Hired', color: '#00BCD4' },
                { value: 'Rejected', label: 'Rejected', color: '#F44336' },
            ],
            pipelineGroupsData: {},
            // Edit candidate
            editCandidateDialog: false,
            editCandidateForm: {},
            savingCandidate: false,
            // Delete confirmation
            confirmDeleteDialog: false,
            deleteItem: null,
            deleteItemType: null, // 'request' or 'candidate'
            deletingItem: false,

        };
    },
    computed: {
        ...mapGetters({
            get_user: 'auth/get_user',
        }),
        isCeo() {
            const ceoEmail = process.env.VUE_APP_CEO_EMAIL;
            return ceoEmail && this.get_user && this.get_user.email === ceoEmail;
        },
        statusFilterOptions() {
            return [
                { text: 'All Statuses', value: 'all' },
                ...this.jobStatuses.map(s => ({ text: s, value: s }))
            ];
        },
        departmentFilterOptions() {
            const depts = [...new Set(this.jobRequests.map(j => j.department).filter(Boolean))];
            return [
                { text: 'All Departments', value: 'all' },
                ...depts.map(d => ({ text: d, value: d }))
            ];
        },
        filteredRequests() {
            return this.jobRequests.filter(j => {
                if (this.search) {
                    const s = this.search.toLowerCase();
                    if (!j.job_title?.toLowerCase().includes(s) && !j.hiring_manager_name?.toLowerCase().includes(s)) return false;
                }
                if (this.statusFilter !== 'all' && j.status !== this.statusFilter) return false;
                if (this.departmentFilter !== 'all' && j.department !== this.departmentFilter) return false;
                return true;
            });
        },
        pendingApprovalCount() {
            const candidates = this.deanCandidatesFromAll.length > 0 ? this.deanCandidatesFromAll : this.deanCandidates;
            return candidates.filter(c => c.dean_approval_status === 'pending').length;
        },
        deanCandidatesFromAll() {
            return this.allCandidates.filter(c => c.submitted_for_dean_approval || c.dean_approval_status);
        },
        filteredDeanCandidates() {
            const candidates = this.deanCandidatesFromAll.length > 0 ? this.deanCandidatesFromAll : this.deanCandidates;
            if (this.deanFilter === 'all') return candidates;
            return candidates.filter(c => c.dean_approval_status === this.deanFilter);
        },
        avgDaysToHire() {
            const closed = this.jobRequests.filter(r => r.status === 'Closed' && r.created_at && r.closed_at);
            if (closed.length === 0) return '—';
            const total = closed.reduce((sum, r) => {
                const days = this.$date(r.closed_at).diff(this.$date(r.created_at), 'day');
                return sum + days;
            }, 0);
            return Math.round(total / closed.length);
        },
        candidatesBySource() {
            const map = {};
            this.allCandidates.forEach(c => {
                const src = c.source || 'Unknown';
                map[src] = (map[src] || 0) + 1;
            });
            return map;
        },
        fileViewerIsPdf() {
            if (!this.fileViewerUrl) return false;
            return this.fileViewerUrl.split('?')[0].toLowerCase().endsWith('.pdf');
        },
        fileViewerIsImage() {
            if (!this.fileViewerUrl) return false;
            return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(this.fileViewerUrl.split('?')[0]);
        },
    },
    watch: {
        fileViewerDialog(val) {
            if (!val) this.cleanupFileViewer();
        },
        deanDetailDialog(val) {
            if (!val) this.analyzingDeanCv = false;
        },
        allCandidates: {
            handler() {
                this.rebuildPipelineGroups();
            },
            immediate: true,
        },
    },
    mounted() {
        document.title = 'DG Tasks - Recruitment';
        this.fetchJobRequests();
        this.fetchDeanCandidates();
        this.fetchAllCandidates();
        window.addEventListener('recruitment:job-request-created', this.handleExternalJobRequestCreated);
    },
    beforeDestroy() {
        window.removeEventListener('recruitment:job-request-created', this.handleExternalJobRequestCreated);
    },
    methods: {
        handleExternalJobRequestCreated() {
            this.fetchJobRequests();
        },
        storageUrl(path) {
            if (!path) return '';
            if (path.startsWith('http://') || path.startsWith('https://')) return path;
            const base = (process.env.VUE_APP_API_URL || '').replace(/\/$/, '');
            const clean = path.replace(/^\/+/, '');
            return `${base}/api/recruitment/files/${clean}`;
        },
        getEmptyRequest() {
            return {
                job_title: '',
                department: '',
                portfolio: '',
                priority: 'Normal',
                employment_type: 'Full-Time',
                hiring_manager_name: '',
                requester_email: '',
                reason_for_hire: '',
                job_description: '',
                experience: '',
                salary_currency: '$ USD',
                salary_range: '',
                success_criteria: '',
                mandatory_skills: '',
                other_mandatory_skills: '',
                preferred_qualifications: '',
                nationality_preference: '',
                attribute_technical_skills: 3,
                attribute_cultural_fit: 3,
                attribute_problem_solving: 3,
                attribute_communication: 3,
                attribute_industry_experience: 3,
                interview_panel: '',
                contact_person: '',
                interview_notes: '',
                requires_dean_approval: false,
            };
        },
        // ─── API Methods ───
        async selectJobRequest(request) {
            // Fetch full detail with activity logs and candidates
            try {
                const response = await this.$axios.get(`api/recruitment/job-requests/${request.id}`);
                if (response.data.response) {
                    this.selectedRequest = response.data.data;
                } else {
                    this.selectedRequest = request;
                }
            } catch (error) {
                console.error('Error fetching request detail:', error);
                this.selectedRequest = request;
            }
        },
        async refreshSelectedRequest() {
            if (this.selectedRequest && this.selectedRequest.id) {
                try {
                    const response = await this.$axios.get(`api/recruitment/job-requests/${this.selectedRequest.id}`);
                    if (response.data.response) {
                        this.selectedRequest = response.data.data;
                    }
                } catch (error) {
                    console.error('Error refreshing request:', error);
                }
            }
            // Refresh all tabs in parallel
            this.fetchJobRequests();
            this.fetchAllCandidates();
            this.fetchDeanCandidates();
        },
        async fetchJobRequests() {
            this.loadingRequests = true;
            try {
                const response = await this.$axios.get('api/recruitment/job-requests');
                if (response.data.response) {
                    this.jobRequests = response.data.data || [];
                }
            } catch (error) {
                console.error('Error fetching job requests:', error);
            } finally {
                this.loadingRequests = false;
            }
        },
        async fetchAllCandidates() {
            try {
                const response = await this.$axios.get('api/recruitment/candidates');
                if (response.data.response) {
                    this.allCandidates = response.data.data || [];
                }
            } catch (error) {
                console.error('Error fetching candidates:', error);
            }
        },
        async fetchDeanCandidates() {
            this.loadingDeanCandidates = true;
            this.deanCandidates = [];
            try {
                // Also refresh allCandidates to ensure dean data is fresh
                if (!this.allCandidates.length) {
                    await this.fetchAllCandidates();
                }
                const response = await this.$axios.get('api/recruitment/dean-approvals', { params: { filter: 'all' } });
                if (response.data.response) {
                    this.deanCandidates = response.data.data || [];
                }
            } catch (error) {
                console.error('Error fetching dean candidates:', error);
                this.deanCandidates = [];
            } finally {
                this.loadingDeanCandidates = false;
            }
        },
        async createJobRequest() {
            if (!this.newRequest.job_title) return;
            this.savingRequest = true;
            try {
                const response = await this.$axios.post('api/recruitment/job-requests', this.newRequest);
                if (response.data.response) {
                    this.$toast.success('Job request created');
                    this.newRequestDialog = false;
                    this.newRequestStep = 0;
                    this.newRequest = this.getEmptyRequest();
                    this.fetchJobRequests();
                } else {
                    this.$toast.error(response.data.message || 'Failed to create request');
                }
            } catch (error) {
                console.error('Error creating job request:', error);
                this.$toast.error('Failed to create job request');
            } finally {
                this.savingRequest = false;
            }
        },
        async handleUpdateRequest(updatedRequest) {
            try {
                const response = await this.$axios.post(`api/recruitment/job-requests/${updatedRequest.id}`, updatedRequest);
                if (response.data.response) {
                    this.$toast.success('Request updated');
                    // Update selected request with fresh data from API
                    if (response.data.data) {
                        this.selectedRequest = response.data.data;
                    }
                    this.fetchJobRequests();
                    this.fetchAllCandidates();
                }
            } catch (error) {
                console.error('Error updating request:', error);
                this.$toast.error('Failed to update request');
            }
        },
        async handleDeleteRequest(requestId) {
            try {
                const response = await this.$axios.delete(`api/recruitment/job-requests/${requestId}`);
                if (response.data.response) {
                    this.$toast.success('Request deleted');
                    this.selectedRequest = null;
                    this.fetchJobRequests();
                }
            } catch (error) {
                console.error('Error deleting request:', error);
                this.$toast.error('Failed to delete request');
            }
        },
        promptDeleteRequest(request, event) {
            if (event) event.stopPropagation();
            this.deleteItem = request;
            this.deleteItemType = 'request';
            this.confirmDeleteDialog = true;
        },
        promptDeleteCandidate(candidate, event) {
            if (event) event.stopPropagation();
            this.deleteItem = candidate;
            this.deleteItemType = 'candidate';
            this.confirmDeleteDialog = true;
        },
        async executeDelete() {
            if (!this.deleteItem) return;
            this.deletingItem = true;
            try {
                if (this.deleteItemType === 'request') {
                    const response = await this.$axios.delete(`api/recruitment/job-requests/${this.deleteItem.id}`);
                    if (response.data.response) {
                        this.$toast.success('Job request deleted');
                        this.jobRequests = this.jobRequests.filter(r => r.id !== this.deleteItem.id);
                        if (this.selectedRequest && this.selectedRequest.id === this.deleteItem.id) {
                            this.selectedRequest = null;
                        }
                    }
                } else if (this.deleteItemType === 'candidate') {
                    const response = await this.$axios.delete(`api/recruitment/candidates/${this.deleteItem.id}`);
                    if (response.data.response) {
                        this.$toast.success('Candidate deleted');
                        const deletedCandidateId = this.deleteItem.id;
                        const jobRequestId = this.deleteItem.job_request_id;

                        // Filter out the deleted candidate from all relevant arrays
                        this.allCandidates = this.allCandidates.filter(c => c.id !== deletedCandidateId);
                        this.deanCandidates = this.deanCandidates.filter(c => c.id !== deletedCandidateId);

                        // If a request is currently selected, remove the candidate from its list
                        if (this.selectedRequest && this.selectedRequest.candidates) {
                            this.selectedRequest.candidates = this.selectedRequest.candidates.filter(c => c.id !== deletedCandidateId);
                        }

                        // Find the parent job request and decrement its candidate count
                        const parentRequest = this.jobRequests.find(r => r.id === jobRequestId);
                        if (parentRequest && parentRequest.candidates_count > 0) {
                            parentRequest.candidates_count--;
                        }

                        if (this.deanDetailDialog && this.selectedDeanCandidate?.id === deletedCandidateId) {
                            this.deanDetailDialog = false;
                        }
                    }
                }
                this.confirmDeleteDialog = false;
                this.deleteItem = null;
            } catch (error) {
                console.error('Error deleting:', error);
                this.$toast.error('Failed to delete');
            } finally {
                this.deletingItem = false;
            }
        },
        // ─── File Viewer ───
        async openFileViewer(url, title = 'Document') {
            const fullUrl = this.storageUrl(url);
            this.fileViewerUrl = fullUrl;
            this.fileViewerTitle = title;
            this.fileViewerPdfData = null;
            this.fileViewerBlobUrl = null;
            this.fileViewerError = null;
            this.fileViewerDialog = true;
            this.fileViewerLoading = true;

            try {
                // Get auth token
                const vuex = JSON.parse(localStorage.getItem('vuex'));
                const token = vuex?.auth?.token || '';

                // Fetch file with auth token using raw axios (supports responseType)
                const response = await axios.get(fullUrl, {
                    responseType: 'arraybuffer',
                    headers: {
                        Authorization: token ? 'Bearer ' + token : '',
                    },
                    withCredentials: true,
                });

                const ext = fullUrl.split('?')[0].split('.').pop().toLowerCase();

                if (ext === 'pdf') {
                    this.fileViewerPdfData = response.data;
                } else if (/^(png|jpe?g|gif|webp|bmp|svg)$/.test(ext)) {
                    const blob = new Blob([response.data]);
                    this.fileViewerBlobUrl = URL.createObjectURL(blob);
                } else {
                    this.fileViewerError = 'Unsupported file type: ' + ext;
                }
            } catch (e) {
                console.error('Failed to fetch file:', e);
                this.fileViewerError = 'Failed to load document.';
            } finally {
                this.fileViewerLoading = false;
            }
        },
        cleanupFileViewer() {
            if (this.fileViewerBlobUrl) {
                URL.revokeObjectURL(this.fileViewerBlobUrl);
                this.fileViewerBlobUrl = null;
            }
            this.fileViewerPdfData = null;
            this.fileViewerError = null;
        },
        // ─── AI CV Analysis (Dean Dialog) ───
        async triggerDeanCvAnalysis(candidate) {
            if (!candidate || !candidate.cv_file_url) return;
            this.analyzingDeanCv = true;
            try {
                const fullUrl = this.storageUrl(candidate.cv_file_url);
                // Determine file type from URL
                const ext = fullUrl.split('?')[0].split('.').pop().toLowerCase();

                // Fetch file with auth first
                const token = localStorage.getItem('vuex')
                    ? JSON.parse(localStorage.getItem('vuex'))?.auth?.token
                    : null;
                const fileResp = await axios.get(fullUrl, {
                    responseType: 'arraybuffer',
                    headers: token ? { Authorization: `Bearer ${token}` } : {},
                });

                let extracted;
                if (ext === 'pdf') {
                    extracted = await extractPdfContent(fileResp.data, {
                        maxPages: 10,
                        imageScale: 1.5,
                        imageQuality: 0.75,
                    });
                } else if (['png', 'jpg', 'jpeg', 'webp'].includes(ext)) {
                    const blob = new Blob([fileResp.data], { type: fileResp.headers['content-type'] || 'image/png' });
                    extracted = await extractImageContent(blob);
                } else {
                    this.$toast.error('Unsupported CV file type: ' + ext);
                    return;
                }

                if (!extracted.extractedText && !extracted.images.length) {
                    this.$toast.error('Could not extract any content from the CV');
                    return;
                }

                const response = await this.$axios.post(
                    `api/recruitment/candidates/${candidate.id}/analyze-cv`,
                    {
                        extracted_text: extracted.extractedText,
                        images: extracted.images,
                    }
                );

                if (response.data.response) {
                    this.selectedDeanCandidate = {
                        ...this.selectedDeanCandidate,
                        ai_cv_summary: response.data.data.ai_cv_summary,
                    };
                    // Also update the card in the list
                    const inList = this.deanCandidates.find(c => c.id === candidate.id);
                    if (inList) inList.ai_cv_summary = response.data.data.ai_cv_summary;
                    this.$toast.success('AI CV analysis complete');
                } else {
                    this.$toast.error(response.data.message || 'Analysis failed');
                }
            } catch (error) {
                console.error('Dean CV analysis error:', error);
                this.$toast.error('CV analysis failed: ' + (error.message || 'Unknown error'));
            } finally {
                this.analyzingDeanCv = false;
            }
        },
        openDeanReview(candidate, mode) {
            this.selectedDeanCandidate = candidate;
            this.deanReviewMode = mode;
            this.deanDetailDialog = true;
        },
        async handleDeanDecision(candidate, decision, notes = '') {
            if (!candidate) return;
            this.processingDecision = true;
            try {
                const response = await this.$axios.post(`api/recruitment/candidates/${candidate.id}/dean-decision`, {
                    decision,
                    notes,
                });
                if (response.data.response) {
                    const labels = { approved: 'Candidate approved', rejected: 'Candidate rejected', modify_offer: 'Modification requested' };
                    this.$toast.success(labels[decision] || 'Decision recorded');
                    // Refresh all tabs + selected request detail so everything stays in sync
                    this.refreshSelectedRequest();
                }
            } catch (error) {
                console.error('Error recording decision:', error);
                this.$toast.error('Failed to record decision');
            } finally {
                this.processingDecision = false;
                this.modifyOfferNotes = '';
            }
        },
        // ─── Helpers ───
        getStatusColor(status) {
            const map = {
                'Draft': 'grey',
                'Submitted': 'blue-grey',
                'Job Posted': 'blue',
                'Applicants Under Review': 'indigo',
                'Shortlist Created': 'deep-purple',
                'Interviews In Progress': 'orange',
                'Final Decision Pending': 'amber darken-2',
                'Approved': 'green',
                'Offer Accepted': 'teal',
                'Onboarding In Progress': 'cyan',
                'Closed': 'grey darken-2',
            };
            return map[status] || 'grey';
        },
        getDeanStatusColor(status) {
            const map = {
                pending: 'amber',
                approved: 'success',
                rejected: 'error',
                modify_offer: 'orange',
            };
            return map[status] || 'grey';
        },
        getRequestStatusCount(status) {
            return this.jobRequests.filter(r => r.status === status).length;
        },
        getPipelineCandidates(stageValue) {
            return this.allCandidates.filter(c => c.status === stageValue);
        },
        async onPipelineDrop(evt, targetStage) {
            // @change fires on both source and destination; only handle destination adds
            if (!evt?.added?.element) return;

            const movedItem = evt.added.element;
            if (!movedItem) return;

            const oldStatus = movedItem.status;
            if (oldStatus === targetStage) return;
            // Update status to match the column it was dropped into
            movedItem.status = targetStage;

            try {
                const payload = { status: targetStage };
                // When dragging into Dean's Approval, also flag for dean review
                const deanStage = "For Dean's Approval";
                if (targetStage === deanStage && !movedItem.submitted_for_dean_approval) {
                    payload.submitted_for_dean_approval = true;
                    payload.dean_approval_status = 'pending';
                }
                await this.$axios.post(`api/recruitment/candidates/${movedItem.id}`, payload);
                this.$toast.success(`${movedItem.name} → ${targetStage}`);
                // Sync into allCandidates so other tabs stay correct
                const inAll = this.allCandidates.find(c => c.id === movedItem.id);
                if (inAll) {
                    inAll.status = targetStage;
                    if (payload.submitted_for_dean_approval) {
                        inAll.submitted_for_dean_approval = true;
                        inAll.dean_approval_status = 'pending';
                    }
                }
                // Refresh dean candidates if moved into/out of dean approval stage
                if (targetStage === deanStage || oldStatus === deanStage) {
                    this.fetchDeanCandidates();
                }            } catch (error) {
                console.error('Error updating candidate status:', error);
                this.$toast.error('Failed to update status');
                // Rollback: restore status and rebuild board from source of truth
                movedItem.status = oldStatus;
                this.rebuildPipelineGroups();
            }
        },
        rebuildPipelineGroups() {
            const groups = {};
            this.pipelineStages.forEach(s => { groups[s.value] = []; });
            this.allCandidates.forEach(c => {
                if (groups[c.status]) {
                    groups[c.status].push(c);
                } else {
                    // Candidates with unrecognized status go to first stage
                    groups[this.pipelineStages[0].value].push(c);
                }
            });
            this.pipelineGroupsData = groups;
        },
        openEditCandidate(candidate) {
            this.editCandidateForm = {
                id: candidate.id,
                name: candidate.name || '',
                email: candidate.email || '',
                phone: candidate.phone || '',
                source: candidate.source || 'Job Board',
                status: candidate.status || 'Applied',
                rating: candidate.rating || 0,
                notes: candidate.notes || '',
                salary_expectation: candidate.salary_expectation || '',
            };
            this.editCandidateDialog = true;
        },
        async saveEditCandidate() {
            if (!this.editCandidateForm.id) return;
            this.savingCandidate = true;
            try {
                const response = await this.$axios.post(
                    `api/recruitment/candidates/${this.editCandidateForm.id}`,
                    this.editCandidateForm
                );
                if (response.data.response) {
                    this.$toast.success('Candidate updated');
                    const updated = response.data.data;
                    const id = this.editCandidateForm.id;
                    // Sync allCandidates
                    const idx = this.allCandidates.findIndex(c => c.id === id);
                    if (idx !== -1) this.$set(this.allCandidates, idx, { ...this.allCandidates[idx], ...updated });
                    // Sync deanCandidates
                    const dIdx = this.deanCandidates.findIndex(c => c.id === id);
                    if (dIdx !== -1) this.$set(this.deanCandidates, dIdx, { ...this.deanCandidates[dIdx], ...updated });
                    // Sync selectedDeanCandidate
                    if (this.selectedDeanCandidate?.id === id) {
                        this.selectedDeanCandidate = { ...this.selectedDeanCandidate, ...updated };
                    }
                    // Sync selectedRequest.candidates
                    if (this.selectedRequest?.candidates) {
                        const sIdx = this.selectedRequest.candidates.findIndex(c => c.id === id);
                        if (sIdx !== -1) this.$set(this.selectedRequest.candidates, sIdx, { ...this.selectedRequest.candidates[sIdx], ...updated });
                    }
                    this.rebuildPipelineGroups();
                    this.editCandidateDialog = false;
                } else {
                    this.$toast.error(response.data.message || 'Failed to update candidate');
                }
            } catch (error) {
                console.error('Error updating candidate:', error);
                this.$toast.error('Failed to update candidate');
            } finally {
                this.savingCandidate = false;
            }
        },
    },
};
</script>

<style scoped>
.cursor-pointer {
    cursor: pointer;
}
.pipeline-card {
    cursor: grab;
    transition: box-shadow 0.2s, transform 0.2s;
}
.pipeline-card:active {
    cursor: grabbing;
}
.pipeline-card:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}
.pipeline-ghost {
    opacity: 0.4;
    background: #E3F2FD;
    border: 2px dashed #1976D2 !important;
}
.pipeline-drag {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
    transform: rotate(2deg);
}
.pipeline-drop-zone {
    transition: background-color 0.2s;
}
.pipeline-card .pipeline-card-delete {
    opacity: 1;
}
</style>
